import { trpc } from "@/lib/trpc";
import { Search, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { PendingQboChanges } from "./-pending-qbo-changes";

export function CustomerMappingTab() {
	const [page, setPage] = useState(1);
	const [search, setSearch] = useState("");
	const pageSize = 50;
	const { data: customersData } = trpc.customers.list.useQuery({ page, pageSize });
	const { data: qboCustomers, isLoading: customersLoading } = trpc.qbo.listCustomers.useQuery();
	const mapMutation = trpc.qbo.mapCustomerToQbo.useMutation();
	const syncMutation = trpc.qbo.syncCustomers.useMutation();
	const utils = trpc.useUtils();
	const [saving, setSaving] = useState<string | null>(null);
	const [syncing, setSyncing] = useState(false);
	const totalPages = customersData ? Math.ceil(customersData.total / pageSize) : 0;

	// W-16 — client-side substring filter over the CURRENT PAGE's customer
	// list. With ≤60 customers per page this is O(n) and instant. We
	// intentionally do NOT server-side filter: the audit calls out that
	// per-list-size the backend roundtrip adds latency without benefit.
	const visibleRows = (customersData?.items ?? []).filter((c) =>
		c.name.toLowerCase().includes(search.toLowerCase()),
	);

	async function handleMap(customerId: string, qboCustomerId: string | null) {
		setSaving(customerId);
		try {
			await mapMutation.mutateAsync({ customerId, qboCustomerId });
			await utils.customers.list.invalidate();
			toast.success("Customer mapping updated");
		} catch (error) {
			toast.error(error instanceof Error ? error.message : "Failed to update mapping");
		} finally {
			setSaving(null);
		}
	}

	async function handleSync() {
		setSyncing(true);
		try {
			const result = await syncMutation.mutateAsync();
			await Promise.all([utils.customers.list.invalidate(), utils.qbo.listCustomers.invalidate()]);
			const parts: string[] = [];
			if (result.imported.length > 0) parts.push(`${result.imported.length} imported`);
			if (result.mapped.length > 0) parts.push(`${result.mapped.length} auto-mapped`);
			if (result.alreadyMapped > 0) parts.push(`${result.alreadyMapped} already synced`);
			toast.success(parts.length > 0 ? `Sync: ${parts.join(", ")}` : "Already in sync");
		} catch (error) {
			toast.error(error instanceof Error ? error.message : "Sync failed");
		} finally {
			setSyncing(false);
		}
	}

	return (
		<>
			<PendingQboChanges />
			<div className="rounded-lg border border-[#e5e7eb] bg-white">
				<div className="flex items-center justify-between border-b border-[#e5e7eb] px-6 py-4">
					<div>
						<h2 className="text-xs font-medium uppercase tracking-wide text-[#6b7280]">
							Customer Mapping
						</h2>
						<p className="mt-1 text-xs text-[#9ca3af]">
							Map PayrollMart customers to QuickBooks customers for invoice sync.
						</p>
					</div>
					<button
						type="button"
						onClick={handleSync}
						disabled={syncing}
						className="rounded bg-[#4f46e5] px-4 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-[#4338ca] disabled:opacity-50"
					>
						{syncing ? "Syncing..." : "Sync from QuickBooks"}
					</button>
				</div>

				{customersLoading ? (
					<div className="p-6">
						<div className="h-8 w-full animate-pulse rounded bg-[#f3f4f6]" />
					</div>
				) : (
					<>
						{/* W-16 — client-side name search. Matches the pattern used on
					    /customers and /documents (Search icon left, X clear button
					    when input has content). Filters `customersData.items` by
					    case-insensitive substring on `name`. */}
						<div className="border-b border-[#f3f4f6] px-6 py-3">
							<div className="relative max-w-sm">
								<Search
									className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9ca3af]"
									strokeWidth={1.5}
								/>
								<input
									type="search"
									placeholder="Search customers..."
									value={search}
									onChange={(e) => setSearch(e.target.value)}
									className="h-9 w-full rounded border border-[#d1d5db] bg-white pl-9 pr-9 text-sm text-[#1e1e20] placeholder:text-[#9ca3af] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
								/>
								{search.length > 0 && (
									<button
										type="button"
										onClick={() => setSearch("")}
										aria-label="Clear search"
										className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-[#9ca3af] hover:bg-[#f3f4f6] hover:text-[#6b7280]"
									>
										<X className="h-3.5 w-3.5" strokeWidth={1.5} />
									</button>
								)}
							</div>
						</div>
						<table className="w-full text-sm">
							<thead>
								<tr className="border-b border-[#f3f4f6] bg-[#f8f9fb]">
									<th className="px-6 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-[#6b7280]">
										PayrollMart Customer
									</th>
									<th className="px-6 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-[#6b7280]">
										QuickBooks Customer
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-[#f3f4f6]">
								{visibleRows.length === 0 ? (
									<tr>
										<td colSpan={2} className="px-6 py-8 text-center text-xs text-[#9ca3af]">
											{search.length > 0
												? `No customers match "${search}".`
												: "No customers found."}
										</td>
									</tr>
								) : (
									visibleRows.map((customer) => (
										<tr key={customer.id}>
											<td className="px-6 py-3 font-medium text-[#1e1e20]">{customer.name}</td>
											<td className="px-6 py-3">
												<select
													value={customer.qboCustomerId ?? ""}
													onChange={(e) => handleMap(customer.id, e.target.value || null)}
													disabled={saving === customer.id}
													className="h-8 w-full max-w-xs rounded border border-[#d1d5db] bg-white px-2 text-sm text-[#1e1e20] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 disabled:opacity-50"
												>
													<option value="">-- Not mapped --</option>
													{qboCustomers?.map((cust) => (
														<option key={cust.id} value={cust.id}>
															{cust.name}
														</option>
													))}
												</select>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</>
				)}

				{totalPages > 1 && (
					<div className="flex items-center justify-between border-t border-[#e5e7eb] px-6 py-3">
						<p className="text-sm text-[#6b7280]">
							Showing {(page - 1) * pageSize + 1}–
							{Math.min(page * pageSize, customersData?.total ?? 0)} of {customersData?.total ?? 0}
						</p>
						<div className="flex items-center gap-1">
							<button
								type="button"
								onClick={() => setPage((p) => Math.max(1, p - 1))}
								disabled={page === 1}
								className="inline-flex h-8 w-8 items-center justify-center rounded border border-[#d1d5db] bg-white text-[#6b7280] transition-colors hover:bg-[#f8f9fb] disabled:opacity-40"
							>
								&#8249;
							</button>
							{Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
								<button
									key={p}
									type="button"
									onClick={() => setPage(p)}
									className={`inline-flex h-8 w-8 items-center justify-center rounded border text-sm font-medium transition-colors ${
										p === page
											? "border-[#4f46e5] bg-[#4f46e5] text-white"
											: "border-[#d1d5db] bg-white text-[#6b7280] hover:bg-[#f8f9fb]"
									}`}
								>
									{p}
								</button>
							))}
							<button
								type="button"
								onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
								disabled={page === totalPages}
								className="inline-flex h-8 w-8 items-center justify-center rounded border border-[#d1d5db] bg-white text-[#6b7280] transition-colors hover:bg-[#f8f9fb] disabled:opacity-40"
							>
								&#8250;
							</button>
						</div>
					</div>
				)}
			</div>
		</>
	);
}
